// cryptoDataSync.js
// Synchronous, reversible obfuscation for ANY JSON-serializable value
// (numbers, strings, arrays, objects) — for use directly in JSX (e.g. href).
//
// Unlike a Feistel network (which only works on a fixed-size integer), this
// encrypts arbitrary-length data with a SHA-256-based keystream (CTR-mode
// style) and appends a checksum for tamper detection.
//
// Same caveat as always, and it matters just as much here: RAW_KEY ships
// inside your JS bundle and is readable by anyone who inspects it in dev
// tools. This stops casual guessing/tampering of URLs; it is NOT a
// substitute for real server-side secrecy.

const RAW_KEY = "mednet-secret-encryption-key"; // change this

// ---- minimal synchronous SHA-256 ----
function sha256Bytes(bytes) {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  let H = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ]);
  const bitLen = bytes.length * 8;
  const withOne = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  withOne.set(bytes);
  withOne[bytes.length] = 0x80;
  const dv = new DataView(withOne.buffer);
  dv.setUint32(withOne.length - 4, bitLen >>> 0);
  dv.setUint32(withOne.length - 8, Math.floor(bitLen / 4294967296));

  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  const w = new Uint32Array(64);

  for (let chunk = 0; chunk < withOne.length; chunk += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(chunk + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    H = new Uint32Array([
      H[0] + a,
      H[1] + b,
      H[2] + c,
      H[3] + d,
      H[4] + e,
      H[5] + f,
      H[6] + g,
      H[7] + h,
    ]);
  }

  const out = new Uint8Array(32);
  const outDv = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) outDv.setUint32(i * 4, H[i]);
  return out;
}

function concatBytes(...arrs) {
  const total = arrs.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrs) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

// Keystream generated by hashing key || counter for each 32-byte block, then
// XORed against the plaintext bytes (CTR-mode style).
function keystream(key, length) {
  const keyBytes = new TextEncoder().encode(key);
  const out = new Uint8Array(length);
  let offset = 0;
  let counter = 0;
  while (offset < length) {
    const counterBytes = new Uint8Array(4);
    new DataView(counterBytes.buffer).setUint32(0, counter);
    const block = sha256Bytes(concatBytes(keyBytes, counterBytes));
    const n = Math.min(32, length - offset);
    out.set(block.subarray(0, n), offset);
    offset += n;
    counter += 1;
  }
  return out;
}

function xorBytes(a, b) {
  const out = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i] ^ b[i];
  return out;
}

function hmacLike(key, bytes) {
  // Truncated hash-based checksum: sha256(key || bytes), first 8 bytes.
  const keyBytes = new TextEncoder().encode(key);
  return sha256Bytes(concatBytes(keyBytes, bytes)).subarray(0, 8);
}

function toBase64Url(bytes) {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(str) {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad =
    padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---- public API ----
// Accepts numbers, strings, arrays, objects — anything JSON.stringify handles.
export function encodeData(value) {
  const json = JSON.stringify(value);
  const plaintext = new TextEncoder().encode(json);
  const ks = keystream(RAW_KEY, plaintext.length);
  const ciphertext = xorBytes(plaintext, ks);
  const tag = hmacLike(RAW_KEY, ciphertext); // 8-byte tamper-detection tag

  return toBase64Url(concatBytes(tag, ciphertext));
}

export function decodeData(encoded) {
  let bytes;
  try {
    bytes = fromBase64Url(encoded);
  } catch {
    throw new Error("Invalid encoded value");
  }
  if (bytes.length < 8) throw new Error("Invalid encoded value");

  const tag = bytes.subarray(0, 8);
  const ciphertext = bytes.subarray(8);

  const expectedTag = hmacLike(RAW_KEY, ciphertext);
  for (let i = 0; i < 8; i++) {
    if (tag[i] !== expectedTag[i])
      throw new Error("Invalid or tampered encoded value");
  }

  const ks = keystream(RAW_KEY, ciphertext.length);
  const plaintext = xorBytes(ciphertext, ks);
  const json = new TextDecoder().decode(plaintext);

  try {
    return JSON.parse(json);
  } catch {
    throw new Error("Invalid or tampered encoded value");
  }
}

// Convenience aliases matching the earlier single-integer API, for drop-in use
// where you only ever pass a single id.
export const encryptId = encodeData;
export const decryptId = decodeData;
export const encryptData = encodeData;
export const decryptData = decodeData;
